import { SessionExpiredError } from "@/common/errors/session-expired-error";
import { showApiErrorToast } from "@/common/helpers/show-api-error-toast";
import { useAppVersionRefresh } from "@/common/hooks/use-app-version-refresh";
import { useErrorDeduplication } from "@/common/hooks/use-error-deduplication";
import { applyThemeToDOM } from "@/common/utils/apply-theme";
import { clearAxiosHeaders } from "@/config/axios";
import Sentry from "@/config/sentry";
import { Theme, userConfigStore } from "@/store/user-config-store";
import { userStore } from "@/store/user-store";
import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { AxiosError, InternalAxiosRequestConfig } from "axios";
import i18next from "i18next";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "wouter";
import { useLogVersions } from "./use-log-versions";

const SKIP_ERROR_HANDLING_STATUS = [400, 401, 403, 404, 408, 409, 422, 429, 499];
const SKIP_ERROR_HANDLING_CODES = [
  "AbortError",
  "CanceledError",
  "ECONNABORTED",
  "ERR_CANCELED",
  "ERR_CONNECTION_REFUSED",
  "ERR_NETWORK",
  "TIMEOUT",
  "EAI_AGAIN",
  "ENOTFOUND",
  "ECONNRESET",
  "ERR_INTERNET_DISCONNECTED",
  "ERR_SSL_PROTOCOL_ERROR",
];

export function useApp() {
  const { theme, lightAccentScheme, darkAccentScheme, lightBaseScheme, darkBaseScheme } = userConfigStore();

  const themeSettings = useMemo(
    () => ({ theme, lightAccentScheme, darkAccentScheme, lightBaseScheme, darkBaseScheme }),
    [theme, lightAccentScheme, darkAccentScheme, lightBaseScheme, darkBaseScheme],
  );
  const [, navigate] = useLocation();
  const { shouldShowError } = useErrorDeduplication();
  const { instance, inProgress } = useMsal();
  const { clearUser } = userStore();
  const pendingLoginRedirectRef = useRef(false);

  useLogVersions();
  useAppVersionRefresh();

  useEffect(() => {
    const checkLoginRedirect = async () => {
      if (pendingLoginRedirectRef.current && inProgress === InteractionStatus.None) {
        pendingLoginRedirectRef.current = false;
        await instance.loginRedirect().catch((error) => {
          console.error("Login redirect failed: ", error);
          pendingLoginRedirectRef.current = true;
        });
      }
    };
    checkLoginRedirect();
  }, [inProgress, instance]);

  const applyCurrentTheme = useCallback(() => {
    applyThemeToDOM(themeSettings);
  }, [themeSettings]);

  useEffect(() => {
    applyCurrentTheme();
  }, [applyCurrentTheme]);

  useEffect(() => {
    if (theme !== Theme.System) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      applyThemeToDOM(themeSettings);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme, themeSettings]);

  const handleFetchError = useCallback(
    async (error: AxiosError | SessionExpiredError) => {
      if (error instanceof SessionExpiredError) {
        clearUser();
        clearAxiosHeaders();
        try {
          await instance.clearCache();
        } catch (cacheError) {
          console.error("Failed to clear MSAL cache: ", cacheError);
          Sentry.captureException(cacheError);
        }
        if (inProgress === InteractionStatus.None) {
          await instance.loginRedirect().catch((error) => console.error("Login redirect failed: ", error));
        } else {
          pendingLoginRedirectRef.current = true;
        }
        return;
      }

      if (error.status === 403) {
        navigate(getForbiddenRedirectTarget());
        return;
      }
      if (!shouldSkipError(error.status, error.code, error.config)) {
        if (
          shouldShowError({
            status: error.status,
            message: error.message,
          })
        ) {
          const errorMessage = getErrorMessage(error.status);
          showApiErrorToast(errorMessage ?? error.message, error);
        }

        Sentry.captureException(error, { contexts: { reactQuery: { error } } });
      }
    },
    [navigate, shouldShowError, clearUser, inProgress, instance],
  );

  return {
    handleFetchError,
  };
}

function shouldSkipError(status?: number, code?: string, config?: InternalAxiosRequestConfig) {
  return (
    SKIP_ERROR_HANDLING_STATUS.includes(status || 0) ||
    SKIP_ERROR_HANDLING_CODES.includes(code || "") ||
    config?.skipErrorHandling
  );
}

function getForbiddenRedirectTarget() {
  const retryPath = getRetryPath();

  if (!retryPath) {
    return "~/forbidden";
  }

  return `~/forbidden?retry=${encodeURIComponent(retryPath)}`;
}

function getRetryPath() {
  const normalizedPath = normalizeRetryPath(
    `${window.location.pathname}${window.location.search}${window.location.hash}`,
  );

  if (!normalizedPath || normalizedPath.startsWith("/forbidden")) {
    return null;
  }

  return normalizedPath;
}

function normalizeRetryPath(path: string) {
  if (!path) return null;
  if (path.startsWith("~/")) return path.slice(1);
  if (path.startsWith("/")) return path;

  return `/${path}`;
}

function getErrorMessage(status?: number): string | null {
  if (!status) return null;

  if (status >= 500 && status <= 599) {
    return i18next.t("common:ServerErrorOccurred", { status });
  }

  if (status >= 400 && status <= 499) {
    return i18next.t("common:RequestCouldNotBeProcessed", { status });
  }

  if (status >= 300 && status <= 399) {
    return i18next.t("common:RequestedPageNotAvailable", { status });
  }

  return null;
}
